import { useEffect, useState, useCallback } from "react";
import api, { euro, num, downloadFile } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { HardHat, FileSpreadsheet, FileText, X } from "lucide-react";
import ColumnFilter from "@/components/ColumnFilter";
import DataTable from "@/components/DataTable";
import SortLabel from "@/components/SortLabel";
import { useTableSort } from "@/lib/useTableSort";
import { toast } from "sonner";

const runDownload = (path, filename) =>
  downloadFile(path, filename).catch(() => toast.error("Errore durante l'esportazione"));

export default function Subappalto() {
  const [commesse, setCommesse] = useState([]);
  const [commessaId, setCommessaId] = useState("");
  const [tratte, setTratte] = useState([]);
  const [righe, setRighe] = useState([]);

  useEffect(() => { api.get("/commesse").then((r) => setCommesse(r.data)); }, []);

  const load = useCallback((cid) => {
    if (!cid) { setRighe([]); setTratte([]); return; }
    api.get("/tratte", { params: { commessa_id: cid } }).then((r) => setTratte(r.data));
    api.get("/righe", { params: { commessa_id: cid } }).then((r) => setRighe(r.data.filter((x) => (x.quantita_subappalto || 0) > 0)));
  }, []);

  useEffect(() => { load(commessaId); }, [commessaId, load]);

  const trattaName = (id) => tratte.find((t) => t.id === id)?.nome || "—";

  // ---------- Filtri colonne ----------
  const emptyFilters = { tratta: [], azienda: [], inserito: [], codice: "", lavorazione: "", codiceSub: "", voceSub: "", qta: "", prezzo: "", importo: "" };
  const [filters, setFilters] = useState(emptyFilters);
  const setF = (k, v) => setFilters((prev) => ({ ...prev, [k]: v }));
  useEffect(() => { setFilters(emptyFilters); }, [commessaId]); // eslint-disable-line react-hooks/exhaustive-deps

  const uniq = (vals) => [...new Set(vals.filter(Boolean))].sort();
  const aziendeOpts = uniq(righe.map((r) => r.subappaltatore)).map((v) => ({ value: v, label: v }));
  const inseritoOpts = uniq(righe.map((r) => r.created_by)).map((v) => ({ value: v, label: v }));
  const has = (val, f) => f === "" || String(val || "").toLowerCase().includes(f.toLowerCase());
  const geq = (val, f) => f === "" || (Number(val) || 0) >= (parseFloat(f) || 0);
  const inSel = (arr, v) => arr.length === 0 || arr.includes(v);

  const filteredRighe = righe.filter((r) =>
    inSel(filters.tratta, r.tratta_id) &&
    inSel(filters.azienda, r.subappaltatore) &&
    inSel(filters.inserito, r.created_by) &&
    has(r.codice, filters.codice) && has(r.descrizione, filters.lavorazione) &&
    has(r.codice_sub, filters.codiceSub) && has(r.descrizione_sub, filters.voceSub) &&
    geq(r.quantita_subappalto, filters.qta) && geq(r.prezzo_sub, filters.prezzo) && geq(r.importo_sub, filters.importo)
  );
  const hasActiveFilter = Object.values(filters).some((v) => (Array.isArray(v) ? v.length > 0 : v !== ""));

  const sortAccessors = {
    tratta: (r) => trattaName(r.tratta_id),
    codice: (r) => r.codice || "",
    lavorazione: (r) => r.descrizione || "",
    codiceSub: (r) => r.codice_sub || "",
    voceSub: (r) => r.descrizione_sub || "",
    azienda: (r) => r.subappaltatore || "",
    qta: (r) => r.quantita_subappalto || 0,
    prezzo: (r) => r.prezzo_sub || 0,
    importo: (r) => r.importo_sub || 0,
    inserito: (r) => r.created_by || "",
  };
  const totale = filteredRighe.reduce((s, r) => s + (r.importo_sub || 0), 0);
  const columns = [
    { id: "tratta", label: "Tratta", width: 180, sortable: true, sortValue: (r) => trattaName(r.tratta_id), cell: (r) => <span className="font-sans text-slate-700">{trattaName(r.tratta_id)}</span>,
      filter: <ColumnFilter testid="sub-filter-tratta" type="select" label="Tratta" value={filters.tratta} onChange={(v) => setF("tratta", v)} options={tratte.map((t) => ({ value: t.id, label: t.nome }))} /> },
    { id: "codice", label: "Cod. capitolato", width: 130, sortable: true, sortValue: (r) => r.codice || "", cell: (r) => <span className="font-semibold text-blue-800">{r.codice}</span>,
      filter: <ColumnFilter testid="sub-filter-codice" type="text" label="Cod. capitolato" value={filters.codice} onChange={(v) => setF("codice", v)} /> },
    { id: "lavorazione", label: "Lavorazione", width: 240, sortable: true, sortValue: (r) => r.descrizione || "", cell: (r) => <span className="font-sans text-slate-800">{r.descrizione}</span>,
      filter: <ColumnFilter testid="sub-filter-lavorazione" type="text" label="Lavorazione" value={filters.lavorazione} onChange={(v) => setF("lavorazione", v)} /> },
    { id: "codiceSub", label: "Cod. subapp.", width: 130, sortable: true, sortValue: (r) => r.codice_sub || "", cell: (r) => <span className="font-semibold text-amber-700">{r.codice_sub || "—"}</span>,
      filter: <ColumnFilter testid="sub-filter-codicesub" type="text" label="Cod. subapp." value={filters.codiceSub} onChange={(v) => setF("codiceSub", v)} /> },
    { id: "voceSub", label: "Voce subappalto", width: 200, sortable: true, sortValue: (r) => r.descrizione_sub || "", cell: (r) => <span className="font-sans text-slate-700">{r.descrizione_sub || "—"}</span>,
      filter: <ColumnFilter testid="sub-filter-vocesub" type="text" label="Voce subappalto" value={filters.voceSub} onChange={(v) => setF("voceSub", v)} /> },
    { id: "azienda", label: "Azienda", width: 170, sortable: true, sortValue: (r) => r.subappaltatore || "", cell: (r) => <span className="font-sans text-slate-700">{r.subappaltatore || "—"}</span>,
      filter: <ColumnFilter testid="sub-filter-azienda" type="select" label="Azienda" value={filters.azienda} onChange={(v) => setF("azienda", v)} options={aziendeOpts} /> },
    { id: "qta", label: "Q.tà sub", width: 120, align: "right", sortable: true, sortValue: (r) => r.quantita_subappalto || 0, cell: (r) => <span>{num(r.quantita_subappalto)} {r.unita_misura}</span>,
      filter: <ColumnFilter testid="sub-filter-qta" type="number" label="Q.tà sub" value={filters.qta} onChange={(v) => setF("qta", v)} align="end" /> },
    { id: "prezzo", label: "Prezzo sub", width: 130, align: "right", sortable: true, sortValue: (r) => r.prezzo_sub || 0, cell: (r) => <span className="text-slate-600">{euro(r.prezzo_sub)}</span>,
      filter: <ColumnFilter testid="sub-filter-prezzo" type="number" label="Prezzo sub" value={filters.prezzo} onChange={(v) => setF("prezzo", v)} align="end" /> },
    { id: "importo", label: "Importo sub", width: 150, align: "right", sortable: true, sortValue: (r) => r.importo_sub || 0, cell: (r) => <span className="font-semibold text-amber-700">{euro(r.importo_sub)}</span>,
      filter: <ColumnFilter testid="sub-filter-importo" type="number" label="Importo sub" value={filters.importo} onChange={(v) => setF("importo", v)} align="end" /> },
    { id: "inserito", label: "Inserito da", width: 150, sortable: true, sortValue: (r) => r.created_by || "", cell: (r) => <span className="text-xs text-slate-500">{r.created_by || "—"}</span>,
      filter: <ColumnFilter testid="sub-filter-inserito" type="select" label="Inserito da" value={filters.inserito} onChange={(v) => setF("inserito", v)} options={inseritoOpts} align="end" /> },
  ];

  return (
    <>
      
      <div className="flex-1 overflow-auto px-6 py-6 lg:px-8 space-y-5">
        <PageHeader title="Subappalto" subtitle="Dettaglio delle lavorazioni eseguite in subappalto (listino dedicato)">
        {commessaId && righe.length > 0 && (
          <>
            <Button variant="outline" size="sm" data-testid="sub-export-excel"
              onClick={() => runDownload(`/commesse/${commessaId}/subappalto/export/excel`, `subappalto_${commessaId}.xlsx`)}>
              <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel
            </Button>
            <Button variant="outline" size="sm" data-testid="sub-export-pdf"
              onClick={() => runDownload(`/commesse/${commessaId}/subappalto/export/pdf`, `subappalto_${commessaId}.pdf`)}>
              <FileText className="h-4 w-4 mr-1.5" /> PDF
            </Button>
          </>
        )}
      </PageHeader>
        <div className="flex items-end gap-3">
          <div className="w-80">
            <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Commessa</Label>
            <Select value={commessaId} onValueChange={setCommessaId}>
              <SelectTrigger data-testid="sub-select-commessa" className="mt-1.5 bg-white">
                <SelectValue placeholder="Seleziona commessa" />
              </SelectTrigger>
              <SelectContent>
                {commesse.map((c) => <SelectItem key={c.id} value={c.id}>{c.codice} · {c.nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {commessaId && hasActiveFilter && (
            <Button variant="outline" size="sm" data-testid="sub-clear-filters" onClick={() => setFilters(emptyFilters)} className="h-10 gap-1.5 text-slate-500">
              <X className="h-4 w-4" /> Azzera filtri
            </Button>
          )}
        </div>

        {!commessaId ? (
          <EmptyState title="Seleziona una commessa" text="Scegli una commessa per vedere il dettaglio del subappalto." />
        ) : righe.length === 0 ? (
          <EmptyState title="Nessuna lavorazione in subappalto" text="Assegna quote di subappalto dalle righe nella scheda Contabilità." />
        ) : (
          <div className="bg-white rounded-md border border-slate-200 shadow-sm overflow-hidden">
            <DataTable tableKey="subappalto" columns={columns} rows={filteredRighe} rowKey={(r) => r.id}
              rowTestId={(r) => `sub-riga-${r.id}`} containerClassName="max-h-[calc(100vh-320px)]" />
            <div className="flex items-center justify-end gap-3 px-4 py-3 border-t border-slate-200 bg-amber-100/50">
              <span className="text-xs font-semibold uppercase tracking-widest text-slate-600">Totale subappalto</span>
              <span data-testid="sub-totale" className="font-heading font-bold text-amber-700 tabular-nums text-base">{euro(totale)}</span>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

const Th = ({ children, filter, className = "", sortField, sortKey, sortDir, onSort }) => (
  <th className={`px-4 py-3 border-b border-slate-200 ${className}`}>
    <div className="flex items-center justify-center gap-1.5">
      {sortField ? (
        <SortLabel label={children} active={sortKey === sortField} dir={sortDir}
          onClick={() => onSort(sortField)} testid={`sort-${sortField}`} />
      ) : (
        <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">{children}</span>
      )}
      {filter}
    </div>
  </th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 ${className}`}>{children}</td>
);

const EmptyState = ({ title, text }) => (
  <div className="bg-white rounded-md border border-slate-200 shadow-sm py-20 flex flex-col items-center text-center">
    <div className="h-14 w-14 rounded-lg bg-amber-50 flex items-center justify-center mb-4">
      <HardHat className="h-6 w-6 text-amber-500" />
    </div>
    <p className="text-slate-600 font-medium">{title}</p>
    <p className="text-sm text-slate-400 mt-1 max-w-xs">{text}</p>
  </div>
);
